import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DiscountCode } from '../../../models/discountCode';
import { fetchDiscountCodes } from '../../../services/admin/discountCodeService';

export interface CompanyDiscountSectionProps {
  companyId: string;
  getAccessToken: () => string | undefined;
}

function formatAmount(code: DiscountCode): string {
  return code.type === 'percentage' ? `${code.amount}%` : `$${code.amount.toFixed(2)}`;
}

function statusPillClass(status: DiscountCode['status']): string {
  if (status === 'active') {
    return 'bo-status-active';
  }
  if (status === 'expired') {
    return 'bo-status-expired';
  }
  return 'bo-status-inactive';
}

/** Read-only list of discount codes assigned to a single company. */
export function CompanyDiscountSection({ companyId, getAccessToken }: CompanyDiscountSectionProps): React.ReactElement {
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await fetchDiscountCodes(getAccessToken, undefined, companyId);
        if (isMounted) {
          setCodes(rows);
        }
      } catch (err) {
        if (isMounted) {
          setCodes([]);
          setError(err instanceof Error ? err.message : 'Failed to load discount codes.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [companyId, getAccessToken]);

  return (
    <div className="bo-card">
      <div className="bo-admin-table-head">
        <h2 className="bo-admin-section-title">Discount codes</h2>
        <p className="bo-muted">
          Codes assigned to this company. Manage them from the <Link to="/admin/discount-codes">discount codes</Link> screen.
        </p>
      </div>
      {loading ? (
        <p className="bo-muted">Loading discount codes…</p>
      ) : error ? (
        <p className="bo-muted">{error}</p>
      ) : codes.length === 0 ? (
        <p className="bo-muted">No discount codes are assigned to this company yet.</p>
      ) : (
        <div className="bo-admin-table-wrap">
          <table className="bo-admin-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((code) => (
                <tr key={code.id}>
                  <td>
                    <strong>{code.code}</strong>
                  </td>
                  <td>{code.type === 'percentage' ? 'Percentage' : 'Fixed'}</td>
                  <td>{formatAmount(code)}</td>
                  <td>
                    <span className={`bo-status-pill ${statusPillClass(code.status)}`}>{code.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
